import type { DocumentClass } from '@/api/(admin)/document-types/types';
import type { DocumentTypesParams, DocumentTypesQueryInput } from '../-types';
import { DOCUMENT_CLASSES } from '../-types';

/**
 * The two columns the list maps into its `ORDER BY`.
 *
 * `SORT_COLUMNS` in `get-admin-list.ts` holds exactly `title` and `updatedAt`,
 * and an unlisted key falls back to `dt.TITLE` rather than erroring
 * (`SORT_COLUMNS[rawKey] ?? 'dt.TITLE'`). That silent fallback is why the
 * whitelist matters more here than on a route that would 400: a sortable
 * header on any other column would look active and quietly sort by title.
 */
const SORTABLE_FIELDS = new Set(['title', 'updatedAt']);

/** Reads a single-valued filter off the table's multi-value filter map. */
function first(
  filters: Record<string, string[]> | undefined,
  key: string,
): string | undefined {
  const value = filters?.[key]?.[0];
  return value && value.length > 0 ? value : undefined;
}

/**
 * Turns table state into the endpoint's parameters.
 *
 * Unlike the legacy admin lists, **this route's `page` is 1-based** — it does
 * its own `(page - 1) * count` — so the UI's page number goes through
 * unchanged rather than being decremented.
 *
 * The filters are named query params, not a JSON `filter` blob: `name`,
 * `description` and `impactAreaDescription` are `LIKE` matches, `documentClass`
 * is an enum that includes `ALL`, and `isDeprecated` is a boolean.
 */
export function toDocumentTypesParams(
  input: DocumentTypesQueryInput,
): DocumentTypesParams {
  const [field] = (input.sort ?? '').split(':');
  const sort = field && SORTABLE_FIELDS.has(field) ? input.sort : undefined;

  const rawClass = first(input.filters, 'documentClass');
  const documentClass = DOCUMENT_CLASSES.includes(rawClass as DocumentClass)
    ? (rawClass as DocumentClass)
    : undefined;

  const name = first(input.filters, 'title');
  const shortDescription = first(input.filters, 'shortDescription');
  const impactAreaDescription = first(input.filters, 'impactAreaDescription');

  return {
    page: Math.max(1, input.page),
    count: input.limit,
    ...(sort ? { sort } : {}),
    ...(name ? { name } : {}),
    ...(shortDescription ? { shortDescription } : {}),
    ...(impactAreaDescription ? { impactAreaDescription } : {}),
    // Sent only when narrowed: the route's own default is `ALL`.
    ...(documentClass ? { documentClass } : {}),
    isDeprecated: input.isDeprecated,
  };
}

export function buildDocumentTypesQs(params: DocumentTypesParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.name) qs.set('name', params.name);
  if (params.shortDescription) {
    qs.set('shortDescription', params.shortDescription);
  }
  if (params.impactAreaDescription) {
    qs.set('impactAreaDescription', params.impactAreaDescription);
  }
  if (params.documentClass) qs.set('documentClass', params.documentClass);
  // Always sent. Leaving it off would not widen the list — the schema defaults
  // it to `false` — so the active/deprecated choice has to be explicit.
  qs.set('isDeprecated', String(params.isDeprecated));
  return qs.toString();
}
